import type { RootStore } from 'logic';
import { action, computed, makeObservable } from 'mobx';
import { QueueTabName, STEP_TAB_DONE, STEP_TAB_TRIAGE } from './ui-active-session';

export class UiQueueSelectorStore {
  private rootStore: RootStore;

  constructor(rootStore: RootStore) {
    this.rootStore = rootStore;

    makeObservable<this>(this, {
      areAllCardsSelected: computed,
      filteredCardIdsForTab: computed,
      toggleAllCardsInTab: action.bound,
    });
  }

  get filteredCardIdsForTab() {
    const { currentSession } = this.rootStore.sessionsStore;
    if (!currentSession) return [];

    const { selectedQueueTab, isStepModeEnabled, currentStepTabId } =
      this.rootStore.uiActiveSessionStore;

    if (isStepModeEnabled && selectedQueueTab !== QueueTabName.Deleted) {
      let cardIds: string[] = [];

      if (currentStepTabId === STEP_TAB_DONE) {
        cardIds = currentSession.doneCardIdsByStep;
      } else if (currentStepTabId === STEP_TAB_TRIAGE) {
        cardIds = currentSession.getCardIdsByStepId(null);
      } else {
        cardIds = currentSession.getCardIdsByStepId(currentStepTabId);
      }

      return currentSession.getVisible(cardIds).map((card) => card.id);
    }

    if (selectedQueueTab === QueueTabName.InProgress) {
      return currentSession.inProgressVisibleCardIds;
    }
    if (selectedQueueTab === QueueTabName.Done) {
      return currentSession.doneVisibleCardIds;
    }
    if (selectedQueueTab === QueueTabName.Deleted) {
      // For deleted tab, return deleted card ids
      return this.rootStore.deletedCardsStore.deletedCardsList.map((card) => card.id);
    }

    return [];
  }

  toggleAllCardsInTab() {
    const { selectedQueueTab } = this.rootStore.uiActiveSessionStore;
    const filteredIds = this.filteredCardIdsForTab;

    if (selectedQueueTab === QueueTabName.Deleted) {
      // For deleted tab, use deleted cards store
      Object.entries(this.rootStore.deletedCardsStore.deletedCards)
        .filter(([cid, _card]) => filteredIds.includes(cid))
        .forEach(([_id, card]) => card.toggleSelection());
    } else {
      // For active/done tabs, use regular cards store
      Object.entries(this.rootStore.cardsStore.cards)
        .filter(([cid, _card]) => filteredIds.includes(cid))
        .forEach(([_id, card]) => card.toggleSelection());
    }
  }

  get areAllCardsSelected() {
    const availableCards = this.filteredCardIdsForTab.length;
    const selectedCards = this.rootStore.uiActiveSessionStore.selectedCardIdsInCurrentStep.length;

    // If there are no cards available, return false (nothing to select)
    if (availableCards === 0) {
      return false;
    }

    // Return true only if all available cards are selected
    return availableCards === selectedCards;
  }

  get isQueueSelectorChecked() {
    return this.rootStore.uiCardView.isCardFocused || this.areAllCardsSelected;
  }

  get isQueueSelectorDisabled() {
    return this.rootStore.uiCardView.isCardFocused || this.filteredCardIdsForTab.length === 0;
  }

  get isQueueSelectorIndeterminate() {
    return !this.isQueueSelectorChecked && this.rootStore.uiActiveSessionStore.hasSelectedCards;
  }
}
